import React, { useCallback, useEffect, useState } from 'react';
import { Unity, useUnityContext } from "react-unity-webgl";
import styles from "./index.module.css";
import { ArtDetailsDisplay, NavBar, TutorialButton, TutorialPopup } from '../widgets';
import { useDispatch, useSelector} from 'react-redux';
import { addItem } from '../redux/slices/artItemSlice';
import { showArtDetails } from '../redux/slices/artDetailsSlice';
import { RootState } from '../redux/store';
import CartPage from '../pages/cartPage';
import { setCartVisibility } from '../redux/slices/cartVisibilitySlice';

const OpenX: React.FC = () => {
  const dispatch = useDispatch();
  const isCartVisible = useSelector((state: RootState) => state.CartState.isCartVisible);
  const isArtDetailsVisible = useSelector((state: RootState) => state.ArtDetailsVisibility.isVisible);

  const { unityProvider, loadingProgression, sendMessage, isLoaded } = useUnityContext({
          loaderUrl: "build/Build/build.loader.js",
        dataUrl: "build/Build/build.data.unityweb",
        frameworkUrl: "build/Build/build.framework.js.unityweb",
        codeUrl: "build/Build/build.wasm.unityweb",
  });

  const [hasClicked, setHasClicked] = useState(false);
  
  const isMobileDevice = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
  };

const enableInteractions = useCallback(() => {
  if(isLoaded)
  {
    sendMessage("SceneManager", "EnableInteractions");
  }
  // console.log("Interactable REACT function called");
}, [isLoaded, sendMessage]);


  const disableInteractions = () => {
    if(isLoaded)
    {
      sendMessage("SceneManager", "DisableInteractions");
    }
    // console.log("Interactable REACT funcion called");
  }

useEffect(() => {
  if(!isArtDetailsVisible && !isCartVisible)
  {
    enableInteractions();
  }
}, [isArtDetailsVisible, isCartVisible, enableInteractions])

  useEffect(() => {
    let attempts = 0;
    const maxAttempts = 10;
    const retryInterval = 1000;

    const attemptSendMessage = () => {
      if (!isLoaded || loadingProgression !== 1) {
        return;
      }
      const mobileValue = isMobileDevice() ? 1 : 0;
      console.log(`Attempt ${attempts + 1}: Sending mobile status to Unity...`, mobileValue);
      try 
      {
        sendMessage("SceneManager","SetIsMobileBool",mobileValue);
        console.log("Message sent successfully");
      } catch (error) {
        console.warn(`Attempt ${attempts + 1} failed:`, error);
        
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(attemptSendMessage, retryInterval);
        } else {
          console.error("Max attempts reached. Failed to send message to Unity.");
        }
      }
    };

    if (isLoaded && loadingProgression === 1) {
      setTimeout(attemptSendMessage, 2000);
    }

    return () => {
      attempts = maxAttempts;
    };
  }, [isLoaded, loadingProgression, sendMessage]);


  interface UnityShowArtDetailsPage extends Event 
  {
           detail:
            {
                artName: string;
                artistName: string;
                artMedium: string;
                artPrice: string; 
                artSize: string;
                artCreationDate: string;
                artAvailable: string;
                artImage: string;
            };
  }

  useEffect(() =>{
    const handleUnityShowArtDetailsPage = (event: CustomEvent<UnityShowArtDetailsPage>['detail']) => {
      const artDetails = event.detail;
      dispatch(showArtDetails({

        artName: artDetails.artName,
        artistName: artDetails.artistName,
        artMedium: artDetails.artMedium,
        artPrice: artDetails.artPrice,
        artSize: artDetails.artSize,
        artCreationDate: artDetails.artCreationDate,
        artAvailable: artDetails.artAvailable,
        artImage: artDetails.artImage,

      }));
    }

     window.addEventListener('showArtDetails', handleUnityShowArtDetailsPage as EventListener);
    
    return () => {
      window.removeEventListener('showArtDetails', handleUnityShowArtDetailsPage as EventListener);
    };

  }, [dispatch]) 
  

  // Define a type for the custom event
  interface UnityAddToCartEvent extends Event {
    detail: {
      name: string;
      description: string;
      size: string;
      artistName: string;
      price: string;
    };
  }

  // Add an event listener for the unity event
  useEffect(() => {
    const handleUnityAddToCart = (event: CustomEvent<UnityAddToCartEvent['detail']>) => {
      const artDetails = event.detail;

      // Dispatch the addItem action with the art details
      dispatch(addItem({
        itemImage: artDetails.description,
        itemPrice: artDetails.price,
        itemName: artDetails.name,
        artistName: artDetails.artistName, 
        itemSize: artDetails.size,
      }));
      
      // Make cart visible
      // setIsCartVisible(true);
      dispatch(setCartVisibility(true))
      
      console.log("art name: " + artDetails.name);
      console.log("art description: " + artDetails.description);
      console.log("art size: " + artDetails.size);
      console.log("artist name: " + artDetails.artistName);
      console.log("art price: " + artDetails.price);
    };
    
    window.addEventListener('unityAddToCart', handleUnityAddToCart as EventListener);
    
    return () => {
      window.removeEventListener('unityAddToCart', handleUnityAddToCart as EventListener);
    };
  }, [dispatch]);

  const loadingPercentage = Math.round(loadingProgression * 100);

  // const handleClick = () => {
  //   setHasClicked(true);
  // };
  
  useEffect(() => {
    if(loadingProgression === 1) {
      setHasClicked(true)
    }
  }, [loadingProgression]);

  const [enablePopup, setPopupEnabled] = useState(false)

  return (
    <div className={styles.container}>
      {isLoaded && (
        <NavBar
          onSearchClick={disableInteractions}
          onSearchBlur={enableInteractions}
        />
      )}

      <Unity
        style={{
          display: hasClicked ? "block" : "none",
          width: isMobileDevice() ? `${window.innerWidth}px` : "100%",
          height: isMobileDevice() ? `${window.innerHeight}px` : "100%",
          maxWidth: "100%",
          maxHeight: "100%",
          overflow: "hidden",
        }}
        devicePixelRatio={3}
        unityProvider={unityProvider}
      />

      <ArtDetailsDisplay handleCloseButton={enableInteractions} />

      <CartPage />

      {isLoaded && loadingProgression === 1 && (
        <TutorialButton handleClick={() => {setPopupEnabled(true); disableInteractions()}}/>
      )}

        {isLoaded && loadingProgression === 1 && (
          <TutorialPopup showPopup={enablePopup} isMobile={isMobileDevice()} closePopup={() => {setPopupEnabled(false); enableInteractions();}}/>
        )}

    



      {loadingProgression !== 1 && (
        <div className={styles.loading}>
          <h2>Loading Store...</h2>
          <div className={styles.progressBar}>
            <div
              className={styles.progressFill}
              style={{ width: `${loadingPercentage}%` }}
            />
          </div>
          <p>{loadingPercentage}%</p>
        </div>
      )}
    </div>
  );
};

export default OpenX;